import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  clearBulkOrderDetail,
  dispatchAsync,
  fetchBulkOrder,
  isBulkOrderRemarkEnabled,
  selectBulkOrderDetail,
  selectBulkOrderDetailError,
  selectBulkOrderDetailGroups,
  selectBulkOrderDetailLineCount,
  selectBulkOrderDetailLoading,
  selectBulkOrderDetailNotFound,
  selectBulkOrderDetailTotalQuantity,
  selectGridContext,
  setBulkOrderGridContext,
} from '../../common/bulkOrder'
import {
  Box,
  Button,
  CreateOrderButtonsWrapper,
  Link,
  Page,
  Row,
} from '../components'
import ErrorTryAgain from '../components/ErrorTryAgain'
import { SwitchField } from '../components/fields'
import { WrappedLoading } from '../components/Loading'
import { SummaryTable } from '../components/order'
import NotFoundPage from '../pages/NotFoundPage'
import BulkOrderCartProductGroup from './BulkOrderCartProductGroup'
import BulkOrderCountSummary from './BulkOrderCountSummary'
import { SIGN_SWITCH_OPTIONS } from './config'
import { useIsBulkOrderEnabled } from './useIsBulkOrderEnabled'
import {
  CartBox,
  CartContent,
  CartGroupList,
  CartMetaCylinderSign,
  CartMetaFields,
  CartMetaFieldsGroup,
  CartSummary,
  CartSummaryText,
} from './BulkOrderCartPage.styles'

const DetailCylinderSign = styled(CartMetaCylinderSign)`
  margin-right: 0;
`

const BulkOrderDetailPageContent = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const order = useSelector(selectBulkOrderDetail)
  const isLoading = useSelector(selectBulkOrderDetailLoading)
  const hasError = useSelector(selectBulkOrderDetailError)
  const isNotFound = useSelector(selectBulkOrderDetailNotFound)
  const cartGroups = useSelector(selectBulkOrderDetailGroups)
  const cartLineCount = useSelector(selectBulkOrderDetailLineCount)
  const cartTotalQuantity = useSelector(selectBulkOrderDetailTotalQuantity)
  const { cylinderSign } = useSelector(selectGridContext)

  const loadDetail = useCallback(() => {
    if (!id) return Promise.resolve()
    return dispatchAsync(dispatch, fetchBulkOrder(id)).catch(() => {})
  }, [dispatch, id])

  useEffect(() => {
    loadDetail()
    return () => {
      dispatch(clearBulkOrderDetail())
    }
  }, [dispatch, loadDetail])

  if (isNotFound && !isLoading) return <NotFoundPage />

  return (
    <Page metaTitle={t('Bulk order detail')}>
      <Page.Content>
        <CartBox>
          <Box.Header>
            <Box.Title>{t('Bulk order detail')}</Box.Title>
          </Box.Header>

          <CartContent>
            {isLoading && <WrappedLoading type="puff" padding="2rem" />}

            {hasError && !isLoading && <ErrorTryAgain onClick={loadDetail} />}

            {!isLoading && !hasError && order && (
              <>
                <CartMetaFields>
                  <CartMetaFieldsGroup>
                    <SummaryTable
                      values={order}
                      data={[
                        {
                          header: t('reference'),
                          fieldName: 'reference',
                          colProps: { fitContent: true },
                        },
                        {
                          header: t('order id'),
                          fieldName: 'manufacturerId',
                          colProps: { fitContent: true },
                        },
                        isBulkOrderRemarkEnabled() && {
                          header: t('BULK_ORDER_REMARK_FOR_MANUFACTURER'),
                          fieldName: 'remark',
                          colProps: { fitContent: true },
                        },
                      ]}
                    />
                  </CartMetaFieldsGroup>
                  <DetailCylinderSign>
                    <SwitchField
                      name="bulkOrderDetailCylinderSign"
                      label={t('cylinder')}
                      value={cylinderSign}
                      options={SIGN_SWITCH_OPTIONS}
                      required
                      hideRequiredStar
                      onChange={({ value }) =>
                        dispatch(setBulkOrderGridContext({ cylinderSign: value }))
                      }
                    />
                  </DetailCylinderSign>
                </CartMetaFields>

                <CartGroupList>
                  {cartGroups.map(group => (
                    <BulkOrderCartProductGroup
                      key={group.lensId}
                      group={group}
                      defaultExpanded={false}
                      readOnly
                    />
                  ))}
                </CartGroupList>
                <CartSummary>
                  <CartSummaryText>
                    <BulkOrderCountSummary
                      productCount={cartGroups.length}
                      variantCount={cartLineCount}
                      pieceCount={cartTotalQuantity}
                    />
                  </CartSummaryText>
                </CartSummary>
              </>
            )}
          </CartContent>
        </CartBox>

        <CreateOrderButtonsWrapper>
          <Row alignItems="stretch">
            <Button.StepBack to={Link.BULK_ORDERS}>{t('back')}</Button.StepBack>
          </Row>
        </CreateOrderButtonsWrapper>
      </Page.Content>
    </Page>
  )
}

const BulkOrderDetailPage = () => {
  const isBulkOrderEnabledForUser = useIsBulkOrderEnabled()

  if (!isBulkOrderEnabledForUser) {
    return <Navigate to={Link.HOME} replace />
  }

  return <BulkOrderDetailPageContent />
}

export default BulkOrderDetailPage
